// src/services/supabaseService.js
import { supabase as supabaseAuth } from '../config/supabase';
import { storageProxy } from './storageProxy';
import db from './dbProxy';
import logger from '../utils/logger';

// ============================================================
// PROXY CLIENT: Reemplaza supabase.from() con db() (proxy /api/v1)
// ============================================================
const supabase = {
  auth: supabaseAuth.auth,
  storage: supabaseAuth.storage,
  from: (table) => {
    const query = db(table);
    // Proxy transparente: agrega .then()/.catch() para que sea awaitable
    // directamente igual que supabase (sin necesidad de .execute() explícito).
    const makeAwaitable = (q) => new Proxy(q, {
      get(target, prop) {
        if (prop === 'then' || prop === 'catch' || prop === 'finally') {
          const p = target.execute();
          return p[prop].bind(p);
        }
        const val = typeof target[prop] === 'function'
          ? (...args) => {
              const result = target[prop](...args);
              // Si el resultado es el mismo builder (chainable), seguir wrapeando
              if (result && typeof result.execute === 'function') return makeAwaitable(result);
              return result;
            }
          : target[prop];
        return val;
      }
    });
    return makeAwaitable(query);
  }
};

// Genera un slug amigable a partir de un nombre
export const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // quitar acentos
    .replace(/[^a-z0-9\s-]/g, '') // quitar caracteres especiales
    .trim()
    .replace(/\s+/g, '-') // espacios → guiones
    .replace(/-+/g, '-'); // guiones múltiples → uno
};

// Función auxiliar para obtener imagen de una entidad
const getImageUrl = async (entityType, entityId) => {
  try {
    const { data, error } = await supabase
      .from('images')
      .select('image_url')
      .eq('entity_type', entityType)
      .eq('entity_id', String(entityId))
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (error) {
      console.error(`Error obteniendo imagen para ${entityType} ${entityId}:`, error?.message);
      return null;
    }
    
    // Si no hay resultados, devolver null
    if (!data || data.length === 0) {
      return null;
    }
    
    return data[0].image_url || null;
  } catch (error) {
    console.error(`Error obteniendo imagen para ${entityType} ${entityId}:`, error?.message);
    return null;
  }
};

// Función auxiliar para reintentos con delay exponencial
const retryWithDelay = async (fn, maxRetries = 3, initialDelay = 500) => {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      // No reintentar si es un error de validación o autenticación
      if (error.message && (error.message.includes('401') || error.message.includes('403'))) {
        throw error;
      }
      // Si es el último intento, lanzar el error
      if (attempt === maxRetries) {
        throw error;
      }
      // Esperar antes de reintentar (delay exponencial: 500ms, 1s, 2s)
      const delay = initialDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};

// Servicio para manejar imágenes
export const imageService = {
  async uploadImage(file, entityType, entityId) {
    try {
      
      // Verificar que entityType sea válido según la tabla images
      const validEntityTypes = ['event', 'club', 'gymnast'];
      if (!validEntityTypes.includes(entityType)) {
        throw new Error(`Tipo de entidad inválido: ${entityType}`);
      }
      
      // Generar nombre único para el archivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${entityType}_${entityId}_${Date.now()}.${fileExt}`;
      const filePath = `${entityType}s/${fileName}`;

      
      // Subir archivo al storage via proxy
      const uploadResult = await storageProxy.upload('gymnastics-images', filePath, file);

      if (!uploadResult.success) {
        logger.error('Error al subir archivo:', uploadResult.error);
        throw new Error(uploadResult.error);
      }


      // Obtener URL pública
      const publicUrl = uploadResult.data?.publicUrl;


      // Primero, eliminar cualquier imagen existente para esta entidad
      await supabase
        .from('images')
        .delete()
        .eq('entity_type', entityType)
        .eq('entity_id', String(entityId));

      // Guardar referencia en la tabla images
      const { data, error } = await supabase
        .from('images')
        .insert([{
          entity_type: entityType,
          entity_id: String(entityId),
          image_url: publicUrl,
          file_path: filePath
        }])
        .select()
        .single();

      if (error) {
        logger.error('Error al guardar referencia en base de datos:', error?.message);
        throw error;
      }

      // También actualizar la columna de URL en la tabla específica
      // para evitar queries adicionales al cargar listas de entidades.
      try {
        const tableFieldMap = {
          gymnast: { table: 'gimnasts', field: 'photo_url' },
          coach:   { table: 'coaches',  field: 'photo_url' },
          judge:   { table: 'judges',   field: 'photo_url' },
          event:   { table: 'events',   field: 'image_url' },
          // clubs: no tiene columna image_url, se lee siempre desde tabla images
        };
        const map = tableFieldMap[entityType];
        if (map) {
          const { error: updErr } = await supabase
            .from(map.table)
            .update({ [map.field]: publicUrl })
            .eq('id', entityId);
          if (updErr) logger.warn(`No se pudo actualizar ${map.table}.${map.field}:`, updErr?.message);
        }
      } catch (syncErr) {
        logger.warn('Error sincronizando URL a tabla específica:', syncErr?.message);
      }

      return { success: true, data };
    } catch (error) {
      logger.error('❌ Error subiendo imagen:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async deleteImageByEntity(entityType, entityId) {
    try {
      // Primero obtener la imagen para eliminar del storage
      const { data: images, error: fetchError } = await supabase
        .from('images')
        .select('file_path')
        .eq('entity_type', entityType)
        .eq('entity_id', String(entityId));

      if (fetchError) {
        // Si no hay imágenes, está bien
        if (fetchError.code === 'PGRST116') {
          return { success: true };
        }
        logger.error('Error obteniendo imágenes:', fetchError?.message);
        throw fetchError;
      }

      // Eliminar cada imagen del storage
      if (images && images.length > 0) {
        const filePaths = images.map(img => img.file_path).filter(path => path);
        
        if (filePaths.length > 0) {
          const { error: storageError } = await supabase.storage
            .from('gymnastics-images')
            .remove(filePaths);

          if (storageError) {
            // No lanzamos error aquí para continuar con la eliminación de la base de datos
          }
        }

        // Eliminar registros de la base de datos
        const { error: deleteError } = await supabase
          .from('images')
          .delete()
          .eq('entity_type', entityType)
          .eq('entity_id', String(entityId));

        if (deleteError) {
          logger.error('Error eliminando imágenes de la base de datos:', deleteError?.message);
          throw deleteError;
        }
      }

      return { success: true };
    } catch (error) {
      logger.error('Error eliminando imágenes por entidad:', error?.message);
      return { success: false, error: error.message };
    }
  },

  // Añadir método faltante que se usa en appService.deleteImage
  async deleteImage(imageId) {
    try {
      
      // Primero obtener información de la imagen
      const { data: image, error: fetchError } = await supabase
        .from('images')
        .select('*')
        .eq('id', imageId)
        .single();
      
      if (fetchError) {
        logger.error('Error obteniendo imagen:', fetchError?.message);
        throw fetchError;
      }
      
      // Eliminar del storage
      if (image.file_path) {
        const { error: storageError } = await supabase.storage
          .from('gymnastics-images')
          .remove([image.file_path]);
        
        if (storageError) {
        }
      }
      
      // Eliminar de la base de datos
      const { error: deleteError } = await supabase
        .from('images')
        .delete()
        .eq('id', imageId);
      
      if (deleteError) {
        logger.error('Error eliminando imagen de la base de datos:', deleteError?.message);
        throw deleteError;
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Error eliminando imagen:', error?.message);
      return { success: false, error: error.message };
    }
  }
};

// Servicios para Eventos (sin cambios)
export const eventService = {
  async getAll() {
    try {
      
      const { data: events, error } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) {
        logger.error('Error obteniendo eventos:', error?.message);
        throw error;
      }

      // Enriquecer eventos con imágenes desde la tabla images
      const enrichedEvents = await Promise.all((events || []).map(async (event) => {
        const imageUrl = await getImageUrl('event', event.id);
        return { ...event, image_url: event.image_url || imageUrl };
      }));

      return { data: enrichedEvents, error: null };
    } catch (error) {
      logger.error('Error en eventService.getAll:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getById(eventId) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo evento por ID:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('slug', slug)
        .single();
      if (error) throw error;
      const imageUrl = await getImageUrl('event', data.id);
      return { data: { ...data, image_url: data.image_url || imageUrl }, error: null };
    } catch (error) {
      logger.error('Error obteniendo evento por slug:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async create(event) {
    try {
      
      // Generate slug from name + year
      const year = event.date ? new Date(event.date).getFullYear() : new Date().getFullYear();
      const baseSlug = generateSlug(event.name) + '-' + year;
      // Add short random suffix to avoid collisions
      const slug = baseSlug + '-' + Math.random().toString(36).substring(2, 6);

      const insertData = {
          name: event.name,
          date: event.date,
          location: event.location || '',
          discipline: event.discipline || null,
          category: event.category || null,
          apparatus: event.apparatus || [],
          is_active: event.is_active !== false,
          slug: slug
      };

      // Si se proporciona created_by, asignar el creador
      if (event.created_by) {
        insertData.created_by = event.created_by;
        insertData.created_by_role = event.created_by_role || 'organizer';
      }

      // Servicios adicionales y flujo de aprobación
      if (event.live_support !== undefined) insertData.live_support = !!event.live_support;
      if (event.flyer_service !== undefined) insertData.flyer_service = !!event.flyer_service;
      if (event.streaming_service !== undefined) insertData.streaming_service = !!event.streaming_service;

      // Configuración de puntuación y jueces
      if (event.competition_level) insertData.competition_level = event.competition_level;
      if (event.judges_config && Object.keys(event.judges_config).length > 0) insertData.judges_config = event.judges_config;
      if (event.use_trim_mean !== undefined) insertData.use_trim_mean = !!event.use_trim_mean;
      if (event.scoring_template) insertData.scoring_template = event.scoring_template;

      // Si es creado por organizador, queda pendiente de aprobación e inactivo
      if (event.created_by_role === 'organizer') {
        insertData.approval_status = 'pending_approval';
        insertData.is_active = false;
      } else {
        insertData.approval_status = 'approved';
      }

      let { data, error } = await supabase
        .from('events')
        .insert([insertData])
        .select()
        .single();
      
      // Si falla por columnas created_by que no existen, reintentar sin ellas
      if (error && error.code === 'PGRST204' && error.message?.includes('created_by')) {
        delete insertData.created_by;
        delete insertData.created_by_role;
        const retry = await supabase
          .from('events')
          .insert([insertData])
          .select()
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Obtener eventos creados por un organizador específico
  async getByCreator(creatorId) {
    try {
      
      let { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('created_by', creatorId)
        .order('date', { ascending: false });
      
      // Si falla porque created_by no existe, devolver todos los eventos
      if (error && (error.code === 'PGRST204' || error.message?.includes('created_by'))) {
        const fallback = await supabase
          .from('events')
          .select('*')
          .order('date', { ascending: false });
        data = fallback.data;
        error = fallback.error;
      }

      if (error) throw error;
      
      // Enriquecer eventos con imágenes desde la tabla images
      const enrichedEvents = await Promise.all((data || []).map(async (event) => {
        const imageUrl = await getImageUrl('event', event.id);
        return { ...event, image_url: event.image_url || imageUrl };
      }));

      return { data: enrichedEvents, error: null };
    } catch (error) {
      logger.error('Error obteniendo eventos del organizador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(id, updates) {
    try {
      
      const updateData = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.date !== undefined) updateData.date = updates.date;
      if (updates.location !== undefined) updateData.location = updates.location || '';
      if (updates.discipline !== undefined) updateData.discipline = updates.discipline || null;
      if (updates.category !== undefined) updateData.category = updates.category || null;
      if (updates.apparatus !== undefined) updateData.apparatus = updates.apparatus || [];
      if (updates.is_active !== undefined) updateData.is_active = updates.is_active !== false;
      if (updates.live_support !== undefined) updateData.live_support = !!updates.live_support;
      if (updates.flyer_service !== undefined) updateData.flyer_service = !!updates.flyer_service;
      if (updates.streaming_service !== undefined) updateData.streaming_service = !!updates.streaming_service;
      if (updates.approval_status !== undefined) updateData.approval_status = updates.approval_status;
      if (updates.admin_notes !== undefined) updateData.admin_notes = updates.admin_notes;
      if (updates.approved_at !== undefined) updateData.approved_at = updates.approved_at;
      if (updates.approved_by !== undefined) updateData.approved_by = updates.approved_by;
      if (updates.competition_level !== undefined) updateData.competition_level = updates.competition_level;
      if (updates.judges_config !== undefined) updateData.judges_config = updates.judges_config;
      if (updates.use_trim_mean !== undefined) updateData.use_trim_mean = !!updates.use_trim_mean;
      if (updates.scoring_template !== undefined) updateData.scoring_template = updates.scoring_template;

      const { data, error } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async delete(id) {
    try {
      
      // Eliminar relaciones event_clubs (por si CASCADE no las limpia)
      await db('event_clubs').delete().eq('event_id', id);
      
      // Eliminar imágenes del evento
      await imageService.deleteImageByEntity('event', id);
      
      // Eliminar códigos de acceso del evento
      await db('event_codes').delete().eq('event_id', id);
      
      // Finalmente eliminar el evento
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('Error eliminando evento:', error?.message);
      return { error: error.message };
    }
  },

  // === Event Approval Workflow ===

  // Get all events pending approval (for admin)
  async getPendingApproval() {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('approval_status', 'pending_approval')
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      // Enriquecer con datos del organizador e imágenes
      const enriched = await Promise.all((data || []).map(async (event) => {
        const imageUrl = await getImageUrl('event', event.id);
        let organizer = null;
        if (event.created_by && event.created_by_role === 'organizer') {
          const { data: orgData } = await supabase
            .from('organizers')
            .select('id, name, email, organization, phone')
            .eq('id', event.created_by)
            .single();
          organizer = orgData;
        }
        return { ...event, organizers: organizer, image_url: event.image_url || imageUrl };
      }));

      return { data: enriched, error: null };
    } catch (error) {
      logger.error('Error obteniendo eventos pendientes:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Approve an event (admin action)
  async approveEvent(eventId, adminNotes = '') {
    try {
      const { data, error } = await supabase
        .from('events')
        .update({
          approval_status: 'approved',
          is_active: true,
          admin_notes: adminNotes,
          approved_at: new Date().toISOString()
        })
        .eq('id', eventId)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error aprobando evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Reject an event (admin action)
  async rejectEvent(eventId, adminNotes = '') {
    try {
      const { data, error } = await supabase
        .from('events')
        .update({
          approval_status: 'rejected',
          is_active: false,
          admin_notes: adminNotes,
          approved_at: new Date().toISOString()
        })
        .eq('id', eventId)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error rechazando evento:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// Servicios para Clubes (CORREGIR la función delete)
export const clubService = {
  async getAll() {
    try {
      
      const { data: clubs, error } = await supabase
        .from('clubs')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        logger.error('Error obteniendo clubes:', error?.message);
        throw error;
      }

      return { data: clubs, error: null };
    } catch (error) {
      logger.error('Error en clubService.getAll:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Buscar clubes con paginación (búsqueda on-demand)
  async search(query, page = 1, pageSize = 15) {
    try {
      const q = query.trim().toLowerCase();
      if (!q) return { data: [], total: 0, error: null };


      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Supabase ilike for case-insensitive search
      const { data, error, count } = await supabase
        .from('clubs')
        .select('*', { count: 'exact' })
        .or(`name.ilike.%${q}%,email.ilike.%${q}%,location.ilike.%${q}%`)
        .order('name', { ascending: true })
        .range(from, to);

      if (error) throw error;

      return { data: data || [], total: count || 0, error: null };
    } catch (error) {
      logger.error('Error buscando clubes:', error?.message);
      return { data: null, total: 0, error: error.message };
    }
  },

  async getByEvent(eventId) {
    try {
      
      const { data: eventClubs, error } = await supabase
        .from('event_clubs')
        .select('club_id')
        .eq('event_id', eventId);
      
      if (error) {
        logger.error('Error obteniendo clubes del evento:', error?.message);
        throw error;
      }

      const clubIds = eventClubs.map(ec => ec.club_id);
      
      if (clubIds.length === 0) {
        return { data: [], error: null };
      }
      
      const { data: clubs, error: clubsError } = await supabase
        .from('clubs')
        .select('*')
        .in('id', clubIds)
        .order('name', { ascending: true });
      
      if (clubsError) throw clubsError;

      return { data: clubs, error: null };
    } catch (error) {
      logger.error('Error en clubService.getByEvent:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getById(clubId) {
    try {
      const { data, error } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo club por ID:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async create(club) {
    try {
      
      const { data, error } = await supabase
        .from('clubs')
        .insert([{
          name: club.name,
          location: club.location || ''
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando club:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(id, updates) {
    try {

      const updateData = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.location !== undefined) updateData.location = updates.location;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.disciplines !== undefined) updateData.disciplines = updates.disciplines;
      if (updates.facebook_url !== undefined) updateData.facebook_url = updates.facebook_url;
      if (updates.instagram_url !== undefined) updateData.instagram_url = updates.instagram_url;
      if (updates.twitter_url !== undefined) updateData.twitter_url = updates.twitter_url;
      if (updates.is_active !== undefined) updateData.is_active = updates.is_active;
      if (updates.approval_status !== undefined) updateData.approval_status = updates.approval_status;
      if (updates.admin_notes !== undefined) updateData.admin_notes = updates.admin_notes;
      if (updates.approved_at !== undefined) updateData.approved_at = updates.approved_at;
      if (updates.coach_qr_token !== undefined) updateData.coach_qr_token = updates.coach_qr_token;
      if (updates.coach_qr_token_created_at !== undefined) updateData.coach_qr_token_created_at = updates.coach_qr_token_created_at;
      if (updates.pose_analysis_enabled !== undefined) updateData.pose_analysis_enabled = updates.pose_analysis_enabled;

      const { data, error } = await supabase
        .from('clubs')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        // If new columns don't exist yet, retry without them
        const errMsg = error.message || '';
        if (error.code === 'PGRST204' || error.code === '42703' || errMsg.includes('disciplines') || errMsg.includes('facebook_url') || errMsg.includes('instagram_url') || errMsg.includes('twitter_url') || errMsg.includes('coach_qr_token') || errMsg.includes('pose_analysis_enabled')) {
          delete updateData.disciplines;
          delete updateData.facebook_url;
          delete updateData.instagram_url;
          delete updateData.twitter_url;
          delete updateData.coach_qr_token;
          delete updateData.coach_qr_token_created_at;
          delete updateData.pose_analysis_enabled;
          const { data: data2, error: error2 } = await supabase
            .from('clubs')
            .update(updateData)
            .eq('id', id)
            .select()
            .single();
          if (error2) throw error2;
          return { data: data2, error: null };
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando club:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getByQrToken(clubId, token) {
    try {
      const { data, error } = await supabase
        .from('clubs')
        .select('id, name, coach_qr_token, coach_qr_token_created_at')
        .eq('id', clubId)
        .eq('coach_qr_token', token)
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error validando QR token:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async addToEvent(eventId, clubId) {
    try {
      
      const { data, error } = await supabase
        .from('event_clubs')
        .insert([{
          event_id: eventId,
          club_id: clubId
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error agregando club al evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async removeFromEvent(eventId, clubId) {
    try {
      
      const { error } = await supabase
        .from('event_clubs')
        .delete()
        .eq('event_id', eventId)
        .eq('club_id', clubId);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('Error removiendo club del evento:', error?.message);
      return { error: error.message };
    }
  },

  async delete(id) {
    try {
      
      // IMPORTANTE: Primero debemos eliminar gimnastas relacionados
      // Obtener gimnastas del club - CAMBIO: usar 'gymnasts' en lugar de 'gimnasts'
      const { data: gymnasts, error: gymnastsError } = await supabase
        .from('gimnasts')  // CAMBIO AQUÍ
        .select('id')
        .eq('club_id', id);
      
      if (gymnastsError) {
        logger.error('Error obteniendo gimnastas del club:', gymnastsError?.message);
        throw gymnastsError;
      }
      
      // Eliminar cada gimnasta
      if (gymnasts && gymnasts.length > 0) {
        for (const gymnast of gymnasts) {
          // Usar gymnastService.delete que maneja imágenes
          const { success, error } = await gymnastService.delete(gymnast.id);
          if (!success) {
          }
        }
      }
      
      // Luego eliminar imágenes del club
      await imageService.deleteImageByEntity('club', id);
      
      // Finalmente eliminar el club
      const { error } = await supabase
        .from('clubs')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('Error eliminando club:', error?.message);
      return { error: error.message };
    }
  },

  // === Club Registration Approval ===

  async getPendingRegistrations() {
    try {
      const { data, error } = await supabase
        .from('clubs')
        .select('*')
        .eq('approval_status', 'pending_approval')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo clubes pendientes:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async approveRegistration(clubId, adminNotes = '') {
    try {
      const { data, error } = await supabase
        .from('clubs')
        .update({
          approval_status: 'approved',
          is_active: true,
          admin_notes: adminNotes,
          approved_at: new Date().toISOString()
        })
        .eq('id', clubId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error aprobando club:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async rejectRegistration(clubId, adminNotes = '') {
    try {
      const { data, error } = await supabase
        .from('clubs')
        .update({
          approval_status: 'rejected',
          is_active: false,
          admin_notes: adminNotes,
          approved_at: new Date().toISOString()
        })
        .eq('id', clubId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error rechazando club:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// Servicios para Jueces
export const judgeService = {
  async getAll() {
    try {
      
      const { data: judges, error } = await supabase
        .from('judges')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      
      return { data: judges, error: null };
    } catch (error) {
      logger.error('Error obteniendo jueces:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getById(judgeId) {
    try {
      if (!judgeId || typeof judgeId !== 'string') {
        throw new Error('Judge ID debe ser un string válido');
      }

      
      // Consulta simple sin reintentos para evitar timeouts
      const { data, error } = await supabase
        .from('judges')
        .select('id, name, email, phone, address, bio, experience, photo_url, curriculum_url, is_active, is_requested, created_at')
        .eq('id', judgeId)
        .limit(1);
      
      if (error) {
        logger.error('❌ Error Supabase:', error?.message);
        throw new Error(`Error al buscar juez: ${error.message}`);
      }
      
      if (!data || data.length === 0) {
        throw new Error('Juez no encontrado en el sistema');
      }
      
      return { data: data[0], error: null };
    } catch (error) {
      logger.error('❌ Error en getById:', error.message);
      return { data: null, error };
    }
  },

  async getByEmail(email) {
    try {
      const result = await retryWithDelay(async () => {
        const { data, error } = await supabase
          .from('judges')
          .select('*')
          .eq('email', email)
          .single();
        
        if (error && error.code !== 'PGRST116') throw error;
        return { data, error: null };
      });
      return result;
    } catch (error) {
      logger.error('Error obteniendo juez por email:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async create(judge, isRequested = false, isActive = true) {
    try {
      
      const { data, error } = await supabase
        .from('judges')
        .insert([{
          email: judge.email,
          password_hash: judge.password_hash,
          name: judge.name,
          phone: judge.phone || '',
          address: judge.address || '',
          is_requested: isRequested,
          is_active: isActive
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando juez:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(id, updates) {
    try {
      
      const updateData = {
        name: updates.name,
        phone: updates.phone || '',
        address: updates.address || ''
      };
      
      // Solo actualizar contraseña si se proporciona
      if (updates.password_hash) {
        updateData.password_hash = updates.password_hash;
      }
      
      // Agregar campos de perfil si se proporcionan
      if ('bio' in updates) updateData.bio = updates.bio;
      if ('experience' in updates) updateData.experience = updates.experience;
      if ('photo_url' in updates) updateData.photo_url = updates.photo_url;
      if ('curriculum_url' in updates) updateData.curriculum_url = updates.curriculum_url;
      
      const { data, error } = await supabase
        .from('judges')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando juez:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getEventCount(judgeId) {
    try {
      const { data: eventJudges, error } = await supabase
        .from('event_judges')
        .select('id')
        .eq('judge_id', judgeId);
      
      if (error) throw error;
      
      return { data: eventJudges?.length || 0, error: null };
    } catch (error) {
      logger.error('Error obteniendo conteo de eventos:', error?.message);
      return { data: 0, error: error.message };
    }
  },

  async getEventsByJudge(judgeId) {
    try {
      if (!judgeId) {
        throw new Error('judgeId es requerido');
      }

      
      // Obtener lista de eventos asignados
      const { data: eventJudges, error: eventJudgesError } = await supabase
        .from('event_judges')
        .select('event_id')
        .eq('judge_id', judgeId);
      
      if (eventJudgesError) {
        logger.error('❌ Error obteniendo event_judges:', eventJudgesError?.message);
        throw eventJudgesError;
      }

      if (!eventJudges || eventJudges.length === 0) {
        return { data: [], error: null };
      }

      const eventIds = eventJudges.map(ej => ej.event_id);
      
      // Obtener detalles de eventos
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('id, name, date, location, discipline, category')
        .in('id', eventIds)
        .order('date', { ascending: false });
      
      if (eventsError) {
        logger.error('❌ Error obteniendo detalles de eventos:', eventsError?.message);
        throw eventsError;
      }

      return { data: events || [], error: null };
    } catch (error) {
      logger.error('❌ Error en getEventsByJudge:', error?.message);
      return { data: null, error };
    }
  },

  async addToEvent(eventId, judgeId) {
    try {
      
      const { data, error } = await supabase
        .from('event_judges')
        .insert([{
          event_id: eventId,
          judge_id: judgeId
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error agregando juez al evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async removeFromEvent(eventId, judgeId) {
    try {
      
      const { error } = await supabase
        .from('event_judges')
        .delete()
        .eq('event_id', eventId)
        .eq('judge_id', judgeId);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('Error removiendo juez del evento:', error?.message);
      return { error: error.message };
    }
  },

  async getByEvent(eventId) {
    try {
      
      const { data: eventJudges, error } = await supabase
        .from('event_judges')
        .select('judge_id')
        .eq('event_id', eventId);
      
      if (error) throw error;
      
      const judgeIds = eventJudges.map(ej => ej.judge_id);
      
      if (judgeIds.length === 0) {
        return { data: [], error: null };
      }
      
      const { data: judges, error: judgesError } = await supabase
        .from('judges')
        .select('id, name, email, phone, address')
        .in('id', judgeIds)
        .order('name', { ascending: true });
      
      if (judgesError) throw judgesError;
      
      return { data: judges, error: null };
    } catch (error) {
      logger.error('Error obteniendo jueces del evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async delete(id) {
    try {
      
      // Primero eliminar asociaciones con eventos
      await supabase
        .from('event_judges')
        .delete()
        .eq('judge_id', id);
      
      // Luego eliminar imágenes
      await imageService.deleteImageByEntity('judge', id);
      
      // Finalmente eliminar el juez
      const { error } = await supabase
        .from('judges')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('Error eliminando juez:', error?.message);
      return { error: error.message };
    }
  },

  // Obtener jueces con solicitudes de acceso pendientes (no activos)
  async getPendingAccessRequests() {
    try {
      
      const { data: judges, error } = await supabase
        .from('judges')
        .select('*')
        .eq('is_requested', true)
        .eq('is_active', false)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return { data: judges, error: null };
    } catch (error) {
      logger.error('Error obteniendo solicitudes pendientes:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Activar un juez (aprobar solicitud de acceso)
  async activateJudge(judgeId) {
    try {
      
      const { data, error } = await supabase
        .from('judges')
        .update({ is_active: true })
        .eq('id', judgeId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error activando juez:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Desactivar un juez
  async deactivateJudge(judgeId) {
    try {
      
      const { data, error } = await supabase
        .from('judges')
        .update({ is_active: false })
        .eq('id', judgeId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error desactivando juez:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Obtener solo jueces activos (para login)
  async getActive() {
    try {
      
      const { data: judges, error } = await supabase
        .from('judges')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (error) throw error;
      
      return { data: judges, error: null };
    } catch (error) {
      logger.error('Error obteniendo jueces activos:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Actualizar contraseña de un juez (con hash)
  async updatePassword(judgeId, plainPassword) {
    try {
      
      // Hash de la contraseña
      const encoder = new TextEncoder();
      const data = encoder.encode(plainPassword);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      const { data: updatedJudge, error } = await supabase
        .from('judges')
        .update({ password_hash: passwordHash })
        .eq('id', judgeId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data: updatedJudge, error: null };
    } catch (error) {
      logger.error('Error actualizando contraseña:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// Servicios para Gimnastas - CAMBIAR TODAS LAS REFERENCIAS DE 'gimnasts' A 'gymnasts'
export const gymnastService = {
  async getByClub(clubId) {
    try {
      
      // CAMBIO: usar 'gymnasts' en lugar de 'gimnasts'
      const { data: gymnasts, error } = await supabase
        .from('gimnasts')  // CAMBIO AQUÍ
        .select('*')
        .eq('club_id', clubId)
        .order('name', { ascending: true });
      
      if (error) {
        logger.error('Error obteniendo gimnastas:', error?.message);
        throw error;
      }

      return { data: gymnasts, error: null };
    } catch (error) {
      logger.error('Error en gymnastService.getByClub:', error?.message);
      return { data: null, error: error.message };
    }
  },


 // NUEVO MÉTODO: Buscar gimnastas con filtros
  async searchGymnasts(filters = {}) {
    try {
      
      let query = supabase
        .from('gimnasts')
        .select('*');
      
      // Aplicar filtros dinámicamente
      if (filters.club_id) {
        query = query.eq('club_id', filters.club_id);
      }
      
      if (filters.event_id) {
        // Obtener clubes del evento primero
        const { data: clubs } = await supabase
          .from('clubs')
          .select('id')
          .eq('event_id', filters.event_id);
        
        if (clubs && clubs.length > 0) {
          const clubIds = clubs.map(club => club.id);
          query = query.in('club_id', clubIds);
        }
      }
      
      if (filters.name && filters.name.trim() !== '') {
        query = query.ilike('name', `%${filters.name}%`);
      }
      
      if (filters.level && filters.level.trim() !== '') {
        query = query.eq('level', filters.level);
      }
      
      if (filters.franja && filters.franja.trim() !== '') {
        query = query.eq('franja', filters.franja);
      }
      
      // Ordenar
      query = query.order('name', { ascending: true });
      
      const { data: gymnasts, error } = await query;
      
      if (error) throw error;
      
      return { data: gymnasts, error: null };
    } catch (error) {
      logger.error('Error buscando gimnastas:', error?.message);
      return { data: null, error: error.message };
    }
  },
  
  // NUEVO MÉTODO: Obtener estadísticas
  async getGymnastStats(filters = {}) {
    try {
      let query = supabase
        .from('gimnasts')
        .select('*');
      
      // Aplicar filtros
      if (filters.event_id) {
        const { data: clubs } = await supabase
          .from('clubs')
          .select('id')
          .eq('event_id', filters.event_id);
        
        if (clubs && clubs.length > 0) {
          const clubIds = clubs.map(club => club.id);
          query = query.in('club_id', clubIds);
        }
      }
      
      if (filters.level) {
        query = query.eq('level', filters.level);
      }
      
      const { data: gymnasts, error } = await query;
      
      if (error) throw error;
      
      // Calcular estadísticas
      const stats = {
        total: gymnasts?.length || 0,
        byLevel: {},
        byFranja: {},
        byClub: {},
        ageRange: { min: Infinity, max: 0 },
        averageAge: 0,
        averageScore: 0
      };
      
      if (gymnasts && gymnasts.length > 0) {
        // Helper para calcular edad desde birth_date
        const calcAge = (bd) => {
          if (!bd) return null;
          const b = new Date(bd);
          if (isNaN(b.getTime())) return null;
          const t = new Date();
          let a = t.getFullYear() - b.getFullYear();
          const m = t.getMonth() - b.getMonth();
          if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
          return a;
        };

        // Conteo por nivel
        gymnasts.forEach(g => {
          stats.byLevel[g.level] = (stats.byLevel[g.level] || 0) + 1;
          stats.byFranja[g.franja] = (stats.byFranja[g.franja] || 0) + 1;
          
          // Edades (calculada desde birth_date, fallback a age)
          const age = calcAge(g.birth_date) ?? g.age;
          if (age) {
            stats.ageRange.min = Math.min(stats.ageRange.min, age);
            stats.ageRange.max = Math.max(stats.ageRange.max, age);
          }
          
          // Puntuaciones
          const scores = Object.values(g.scores || {});
          const validScores = scores.filter(s => s > 0);
          if (validScores.length > 0) {
            stats.averageScore += validScores.reduce((a, b) => a + b, 0) / validScores.length;
          }
        });
        
        // Calcular promedios
        const ages = gymnasts.map(g => calcAge(g.birth_date) ?? g.age).filter(a => a && a > 0);
        stats.averageAge = ages.length > 0 ? ages.reduce((s, a) => s + a, 0) / ages.length : 0;
        
        stats.averageScore = stats.averageScore / gymnasts.length || 0;
        
        // Obtener conteo por club
        for (const gymnast of gymnasts) {
          const { data: club } = await supabase
            .from('clubs')
            .select('name')
            .eq('id', gymnast.club_id)
            .single();
          
          if (club) {
            stats.byClub[club.name] = (stats.byClub[club.name] || 0) + 1;
          }
        }
      }
      
      return { data: stats, error: null };
    } catch (error) {
      logger.error('Error obteniendo estadísticas:', error?.message);
      return { data: null, error: error.message };
    }
  },
  

  async getById(gymnastId) {
    try {
      // CAMBIO: usar 'gymnasts' en lugar de 'gimnasts'
      const { data, error } = await supabase
        .from('gimnasts')  // CAMBIO AQUÍ
        .select('*')
        .eq('id', gymnastId)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo gimnasta por ID:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async create(gymnast) {
    try {
      
      // CAMBIO: usar 'gymnasts' en lugar de 'gimnasts'
      const { data, error } = await supabase
        .from('gimnasts')  // CAMBIO AQUÍ
        .insert([{
          name: gymnast.name,
          age: 0,
          birth_date: gymnast.birth_date || null,
          level: gymnast.level,
          franja: gymnast.franja,
          category: gymnast.category || null,
          club_id: gymnast.club_id,
          scores: gymnast.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 }
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando gimnasta:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(id, updates) {
    try {
      
      const updateData = {
        name: updates.name,
        level: updates.level,
        franja: updates.franja,
        club_id: updates.club_id,
        scores: updates.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 }
      };
      
      // Solo incluir category si está definido
      if (updates.category !== undefined) {
        updateData.category = updates.category || null;
      }

      // Solo incluir birth_date si está definido
      if (updates.birth_date !== undefined) {
        updateData.birth_date = updates.birth_date || null;
      }
      // Compatibilidad legacy: si viene age, guardarlo
      if (updates.age !== undefined) {
        updateData.age = updates.age ? parseInt(updates.age) : null;
      }
      
      // CAMBIO: usar 'gymnasts' en lugar de 'gimnasts'
      const { data, error } = await supabase
        .from('gimnasts')  // CAMBIO AQUÍ
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando gimnasta:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async updateScore(gymnastId, partialScores) {
  try {

    // 1️⃣ Obtener scores actuales
    const { data: current, error: fetchError } = await supabase
      .from('gimnasts')
      .select('scores')
      .eq('id', gymnastId)
      .single();

    if (fetchError) throw fetchError;

    const currentScores = current.scores || {};

    // 2️⃣ Merge de scores (NO se pierden los otros)
    const mergedScores = {
      ...currentScores,
      ...partialScores
    };

    // 3️⃣ Recalcular all-round y total
    // ALL-ROUND = Suma de puntajes / Número de aparatos CON PUNTAJE
    // TOTAL = Suma de todos los puntajes
    const scoresArray = Object.values(mergedScores).filter(s => s > 0);
    const total = scoresArray.reduce((sum, s) => sum + Number(s || 0), 0);
    const allRound = scoresArray.length > 0 ? total / scoresArray.length : 0;

    // 4️⃣ Guardar todo
    const { data, error } = await supabase
      .from('gimnasts')
      .update({
        scores: mergedScores,
        total: Number(total.toFixed(2)),
        all_round: Number(allRound.toFixed(2))
      })
      .eq('id', gymnastId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    logger.error('❌ Error actualizando puntuación:', error?.message);
    return { data: null, error: error.message };
  }
},

  async delete(id) {
    try {
      
      // Primero eliminar imágenes asociadas
      await imageService.deleteImageByEntity('gymnast', id);
      
      // CAMBIO: usar 'gymnasts' en lugar de 'gimnasts'
      const { error } = await supabase
        .from('gimnasts')  // CAMBIO AQUÍ
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('Error eliminando gimnasta:', error?.message);
      return { error: error.message };
    }
  }
};

// Servicio principal - CORREGIR loadAllData
export const appService = {
  async loadAllData() {
    try {
      
      // 1. Cargar eventos directamente
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: false });
      
      if (eventsError) {
        logger.error('Error cargando eventos:', eventsError?.message);
        throw eventsError;
      }

      if (!events || events.length === 0) {
        return { events: [], clubs: [], judges: [], error: null };
      }


      // 2. Cargar todos los clubes (globales)
      const { data: allClubs, error: clubsError } = await supabase
        .from('clubs')
        .select('*')
        .order('name', { ascending: true });
      
      if (clubsError) {
        logger.error('Error cargando clubes:', clubsError?.message);
        throw clubsError;
      }


      // 3. Cargar todos los jueces
      const { data: allJudges, error: judgesError } = await supabase
        .from('judges')
        .select('id, name, email, phone, address, is_active, is_requested')
        .order('name', { ascending: true });
      
      if (judgesError) {
        logger.error('Error cargando jueces:', judgesError?.message);
        throw judgesError;
      }


      // 4. Para cada evento, cargar imagen, clubes asociados y jueces asociados
      const eventsWithData = await Promise.all(
        events.map(async (event) => {
          // Obtener imagen del evento
          const eventImageUrl = await getImageUrl('event', event.id);
          
          // Obtener clubes asociados a este evento
          const { data: eventClubs, error: eventClubsError } = await supabase
            .from('event_clubs')
            .select('club_id')
            .eq('event_id', event.id);
          
          if (eventClubsError) {
            logger.error(`Error obteniendo clubes para evento ${event.id}:`, eventClubsError?.message);
            return { 
              ...event, 
              image_url: eventImageUrl,
              clubs: [],
              judges: []
            };
          }

          const clubIds = eventClubs.map(ec => ec.club_id);
          const eventClubsData = allClubs.filter(club => clubIds.includes(club.id));

          // 5. Para cada club del evento, cargar gimnastas
          const clubsWithData = await Promise.all(
            eventClubsData.map(async (club) => {
              const clubImageUrl = await getImageUrl('club', club.id);
              
              const { data: gymnasts, error: gymnastsError } = await supabase
                .from('gimnasts')
                .select('*')
                .eq('club_id', club.id)
                .order('name', { ascending: true });
              
              if (gymnastsError) {
                logger.error(`Error cargando gimnastas para club ${club.id}:`, gymnastsError?.message);
                return { 
                  ...club, 
                  image_url: clubImageUrl,
                  gymnasts: [] 
                };
              }

              const gymnastsWithData = gymnasts ? gymnasts.map((g) => {
                const scoresArray = Object.values(g.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 });
                const calculatedTotal = scoresArray.reduce((sum, s) => sum + s, 0);
                const calculatedAllRound = (calculatedTotal / scoresArray.length);
                
                return {
                  ...g,
                  id: g.id,
                  name: g.name,
                  age: 0,
                  level: g.level || '',
                  franja: g.franja || '',
                  scores: g.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 },
                  total: calculatedTotal,
                  allRound: calculatedAllRound,
                  club_id: g.club_id,
                  image_url: null
                };
              }) : [];

              return {
                ...club,
                image_url: clubImageUrl,
                gymnasts: gymnastsWithData
              };
            })
          );

          // Obtener jueces asociados a este evento
          const { data: eventJudges, error: eventJudgesError } = await supabase
            .from('event_judges')
            .select('judge_id')
            .eq('event_id', event.id);
          
          if (eventJudgesError) {
            logger.error(`Error obteniendo jueces para evento ${event.id}:`, eventJudgesError?.message);
          }

          const judgeIds = eventJudges?.map(ej => ej.judge_id) || [];
          const eventJudgesData = allJudges.filter(judge => judgeIds.includes(judge.id));

          return {
            ...event,
            image_url: eventImageUrl,
            clubs: clubsWithData,
            judges: eventJudgesData
          };
        })
      );

      const totalClubs = eventsWithData.reduce((sum, event) => sum + event.clubs.length, 0);
      const totalGymnasts = eventsWithData.reduce((sum, event) => 
        sum + event.clubs.reduce((clubSum, club) => clubSum + club.gymnasts.length, 0), 0);


      return {
        events: eventsWithData,
        clubs: allClubs,
        judges: allJudges,
        error: null
      };

    } catch (error) {
      logger.error('❌ Error crítico cargando datos:', error?.message);
      return {
        events: [],
        clubs: [],
        judges: [],
        error: error.message
      };
    }
  },

  async loadClubGymnasts(clubId) {
    try {
      
      const { data: gymnasts, error: gymnastsError } = await supabase
        .from('gimnasts')
        .select('*')
        .eq('club_id', clubId)
        .order('name', { ascending: true });
      
      if (gymnastsError) {
        logger.error('Error cargando atletas:', gymnastsError?.message);
        return { gymnasts: [], scores: [], error: gymnastsError };
      }


      // Extraer scores de los gymnasts
      const scores = gymnasts ? gymnasts.flatMap(g =>
        Object.entries(g.scores || {}).map(([apparatus, score]) => ({
          id: `${g.id}-${apparatus}`,
          gymnast_id: g.id,
          club_id: clubId,
          apparatus: apparatus,
          score: score
        }))
      ) : [];

      return {
        gymnasts: gymnasts || [],
        scores: scores,
        error: null
      };
    } catch (error) {
      logger.error('Error cargando atletas del club:', error?.message);
      return { gymnasts: [], scores: [], error: error.message };
    }
  },

  async saveEvent(eventData) {
    try {
      
      let result;
      
      if (eventData.id && !eventData.id.startsWith('temp-')) {
        // Actualizar evento existente
        result = await eventService.update(eventData.id, {
          name: eventData.name,
          date: eventData.date,
          location: eventData.location || '',
          discipline: eventData.discipline || null,
          category: eventData.category || null,
          apparatus: eventData.apparatus || [],
          is_active: eventData.is_active !== false
        });
      } else {
        // Crear nuevo evento
        result = await eventService.create({
          name: eventData.name,
          date: eventData.date,
          location: eventData.location || '',
          discipline: eventData.discipline || null,
          category: eventData.category || null,
          apparatus: eventData.apparatus || [],
          is_active: eventData.is_active !== false
        });
      }
      
      if (result.error) throw new Error(result.error);
      
      const eventId = result.data.id;
      
      // Manejar asociaciones de clubes
      if (eventData.selectedClubs && Array.isArray(eventData.selectedClubs)) {
        // Obtener clubes actuales del evento
        const clubsResult = await clubService.getByEvent(eventId);
        const currentClubs = (clubsResult.data && Array.isArray(clubsResult.data)) ? clubsResult.data : [];
        const currentClubIds = currentClubs.map(c => c.id);
        
        // Agregar nuevos clubes
        for (const clubId of eventData.selectedClubs) {
          if (!currentClubIds.includes(clubId)) {
            await clubService.addToEvent(eventId, clubId);
          }
        }
        
        // Eliminar clubes no seleccionados
        for (const clubId of currentClubIds) {
          if (!eventData.selectedClubs.includes(clubId)) {
            await clubService.removeFromEvent(eventId, clubId);
          }
        }
      }
      
      // Manejar asociaciones de jueces
      if (eventData.selectedJudges && Array.isArray(eventData.selectedJudges)) {
        // Obtener jueces actuales del evento
        const judgesResult = await judgeService.getByEvent(eventId);
        const currentJudges = (judgesResult.data && Array.isArray(judgesResult.data)) ? judgesResult.data : [];
        const currentJudgeIds = currentJudges.map(j => j.id);
        
        // Agregar nuevos jueces
        for (const judgeId of eventData.selectedJudges) {
          if (!currentJudgeIds.includes(judgeId)) {
            await judgeService.addToEvent(eventId, judgeId);
          }
        }
        
        // Eliminar jueces no seleccionados
        for (const judgeId of currentJudgeIds) {
          if (!eventData.selectedJudges.includes(judgeId)) {
            await judgeService.removeFromEvent(eventId, judgeId);
          }
        }
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error saving event:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async saveClub(clubData) {
    try {
      
      let clubResult;
      
      if (clubData.id && !clubData.id.startsWith('temp-')) {
        // Actualizar club existente
        const result = await clubService.update(clubData.id, {
          name: clubData.name,
          location: clubData.location || '',
          event_id: clubData.event_id
        });
        
        if (result.error) throw new Error(result.error);
        clubResult = result.data;
      } else {
        // Crear nuevo club
        const result = await clubService.create({
          name: clubData.name,
          location: clubData.location || '',
          event_id: clubData.event_id
        });
        
        if (result.error) throw new Error(result.error);
        clubResult = result.data;
      }

      // NOTA: Las atletas se manejan por separado en addGymnastToClubForm y removeGymnastFromClubForm
      // NO iteramos a través de todas las atletas aquí para evitar actualizar todos los 156 registros
      // cuando solo se modifica uno

      return { success: true, data: clubResult };
    } catch (error) {
      logger.error('Error saving club:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async deleteEvent(eventId) {
    try {
      const result = await eventService.delete(eventId);
      
      if (result.error) throw new Error(result.error);
      return { success: true };
    } catch (error) {
      logger.error('Error deleting event:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async deleteClub(clubId) {
    try {
      const result = await clubService.delete(clubId);
      
      if (result.error) throw new Error(result.error);
      return { success: true };
    } catch (error) {
      logger.error('Error deleting club:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async saveGymnast(gymnastData) {
    try {
      
      let result;
      
      if (gymnastData.id && !gymnastData.id.startsWith('temp-')) {
        // Actualizar gimnasta existente
        result = await gymnastService.update(gymnastData.id, {
          name: gymnastData.name,
          age: 0,
          level: gymnastData.level,
          franja: gymnastData.franja,
          club_id: gymnastData.club_id,
          scores: gymnastData.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 }
        });
        
        if (result.error) throw new Error(result.error);
      } else {
        // Crear nuevo gimnasta
        result = await gymnastService.create({
          name: gymnastData.name,
          age: 0,
          level: gymnastData.level,
          franja: gymnastData.franja,
          club_id: gymnastData.club_id,
          scores: gymnastData.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 }
        });
        
        if (result.error) throw new Error(result.error);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error saving gymnast:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async deleteGymnast(gymnastId) {
    try {
      const result = await gymnastService.delete(gymnastId);
      
      if (result.error) throw new Error(result.error);
      return { success: true };
    } catch (error) {
      logger.error('Error deleting gymnast:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async updateGymnastScore(gymnastId, scores) {
    try {
      const result = await gymnastService.updateScore(gymnastId, scores);
      
      if (result.error) throw new Error(result.error);
      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error updating gymnast score:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async saveCompetitionScore(gymnastId, eventId, clubId, scores) {
    try {
      const scoresArray = Object.values(scores).filter(s => s > 0);
      const total = scoresArray.reduce((sum, s) => sum + Number(s || 0), 0);

      const { data, error } = await supabase
        .from('competition_scores')
        .upsert({
          gymnast_id: gymnastId,
          event_id: eventId,
          club_id: clubId,
          scores: scores,
          total: Number(total.toFixed(2)),
          updated_at: new Date().toISOString()
        }, { onConflict: 'gymnast_id,event_id' })
        .select()
        .single();

      if (error) {
        logger.error('Error saving competition score:', error?.message);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      logger.error('Error saving competition score:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async getCompetitionScoresByGymnast(gymnastId) {
    try {
      const { data, error } = await supabase
        .from('competition_scores')
        .select('*, events(id, name, date, discipline, category)')
        .eq('gymnast_id', gymnastId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error getting competition scores:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async saveJudge(judgeData) {
    try {
      
      let result;
      
      if (judgeData.id && !judgeData.id.startsWith('temp-')) {
        // Actualizar juez existente
        result = await judgeService.update(judgeData.id, {
          name: judgeData.name,
          phone: judgeData.phone,
          address: judgeData.address
        });
        
        if (result.error) throw new Error(result.error);
      } else {
        // Crear nuevo juez
        result = await judgeService.create({
          name: judgeData.name,
          email: judgeData.email,
          phone: judgeData.phone,
          address: judgeData.address,
          password_hash: judgeData.password || 'temp-hash'
        });
        
        if (result.error) throw new Error(result.error);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error saving judge:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async deleteJudge(judgeId) {
    try {
      const result = await judgeService.delete(judgeId);
      
      if (result.error) throw new Error(result.error);
      return { success: true };
    } catch (error) {
      logger.error('Error deleting judge:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async addJudgeToEvent(eventId, judgeId) {
    try {
      const result = await judgeService.addToEvent(eventId, judgeId);
      
      if (result.error) throw new Error(result.error);
      return { success: true };
    } catch (error) {
      logger.error('Error adding judge to event:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async removeJudgeFromEvent(eventId, judgeId) {
    try {
      const result = await judgeService.removeFromEvent(eventId, judgeId);
      
      if (result.error) throw new Error(result.error);
      return { success: true };
    } catch (error) {
      logger.error('Error removing judge from event:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async uploadImage(entityType, entityId, file) {
    try {
      const result = await imageService.uploadImage(file, entityType, entityId);
      return result;
    } catch (error) {
      logger.error('Error uploading image:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async getImageByEntity(entityType, entityId) {
    try {
      // Construir la ruta esperada en el storage
      const entityTypeMap = {
        'gymnast': 'gymnasts',
        'club': 'clubs',
        'event': 'events'
      };
      
      const folderName = entityTypeMap[entityType] || entityType + 's';
      
      
      // Intentar listar archivos en esa carpeta
      const { data: files, error: listError } = await storageProxy.list('gymnastics-images', folderName, 100);

      if (listError) {
        return { data: null, error: null };
      }

      if (!files || files.length === 0) {
        return { data: null, error: null };
      }

      // Buscar archivo que contenga el ID
      const matchingFile = files.find(file => file.name.includes(entityId));
      
      if (!matchingFile) {
        return { data: null, error: null };
      }

      
      // Obtener la URL pública del archivo
      const filePath = `${folderName}/${matchingFile.name}`;
      
      const { data: publicUrlData } = await storageProxy.getPublicUrl('gymnastics-images', filePath);

      if (publicUrlData && publicUrlData.publicUrl) {
        return { data: { image_url: publicUrlData.publicUrl }, error: null };
      }

      return { data: null, error: null };
    } catch (error) {
      logger.error('Error getting image:', error?.message);
      return { data: null, error: null };
    }
  },

  async deleteImage(imageId) {
    try {
      const result = await imageService.deleteImage(imageId);
      return result;
    } catch (error) {
      logger.error('Error deleting image:', error?.message);
      return { success: false, error: error.message };
    }
  },

  async uploadFile(file, bucket, fileName) {
    try {
      if (!file || !bucket || !fileName) {
        throw new Error('Archivo, bucket y fileName son requeridos');
      }

      // Validar tipo de archivo (foto o PDF)
      const validPhotoTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const validDocTypes = ['application/pdf'];
      const allValidTypes = [...validPhotoTypes, ...validDocTypes];

      if (!allValidTypes.includes(file.type)) {
        throw new Error('Tipo de archivo no permitido. Use JPEG, PNG o PDF');
      }

      // Subir archivo via proxy
      const result = await storageProxy.upload(bucket, fileName, file);

      if (!result.success) {
        throw new Error(`Error subiendo archivo: ${result.error}`);
      }

      // Obtener URL pública del archivo
      const publicUrl = result.data?.publicUrl;

      if (!publicUrl) {
        throw new Error('No se pudo obtener la URL pública del archivo');
      }

      logger.info(`Archivo subido exitosamente: ${fileName}`);

      return { 
        data: { 
          fileName, 
          publicUrl 
        }, 
        error: null 
      };
    } catch (error) {
      logger.error('Error subiendo archivo:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ============================================================
// SERVICIO PARA INVITACIONES Y RESET DE CONTRASEÑA
// ============================================================

export const judgeInvitationService = {
  async generateInvitation(adminId, eventId) {
    try {
      
      // Generar código de invitación de 10 caracteres
      const code = Math.random().toString(36).substring(2, 12).toUpperCase();
      
      // Establecer expiración en 7 días
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      const { data, error } = await supabase
        .from('judge_invitations')
        .insert({
          code,
          admin_id: adminId,
          event_id: eventId,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error generando invitación:', error?.message);
      return { data: null, error };
    }
  },

  async validateInvitation(code) {
    try {
      if (!code || code.length < 5) {
        throw new Error('Código de invitación inválido');
      }

      
      const { data, error } = await supabase
        .from('judge_invitations')
        .select('*')
        .eq('code', code.toUpperCase())
        .single();
      
      if (error) throw new Error('Código de invitación no encontrado');
      
      // Verificar que no esté usado
      if (data.used) {
        throw new Error('Este código de invitación ya ha sido utilizado');
      }
      
      // Verificar que no haya expirado
      if (new Date(data.expires_at) < new Date()) {
        throw new Error('Este código de invitación ha expirado');
      }
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error validando invitación:', error?.message);
      return { data: null, error };
    }
  },

  async markInvitationAsUsed(invitationId, judgeId) {
    try {
      
      const { data, error } = await supabase
        .from('judge_invitations')
        .update({
          used: true,
          judge_id: judgeId,
          used_at: new Date().toISOString()
        })
        .eq('id', invitationId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error marcando invitación:', error?.message);
      return { data: null, error };
    }
  }
};

export const passwordResetService = {
  async requestPasswordReset(judgeId) {
    try {
      if (!judgeId) throw new Error('Judge ID requerido');
      
      
      // Generar token único
      const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
      
      // Establecer expiración en 24 horas
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);
      
      const { data, error } = await supabase
        .from('password_resets')
        .insert({
          judge_id: judgeId,
          token,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error solicitando reset:', error?.message);
      return { data: null, error };
    }
  },

  async validateResetToken(token) {
    try {
      if (!token) throw new Error('Token requerido');
      
      
      const { data, error } = await supabase
        .from('password_resets')
        .select('*')
        .eq('token', token)
        .single();
      
      if (error) throw new Error('Token no válido o no encontrado');
      
      if (data.used) {
        throw new Error('Este token ya ha sido utilizado');
      }
      
      if (new Date(data.expires_at) < new Date()) {
        throw new Error('Este token ha expirado');
      }
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error validando token:', error?.message);
      return { data: null, error };
    }
  },

  async resetPassword(token, newPassword) {
    try {
      if (!token || !newPassword) {
        throw new Error('Token y contraseña requeridos');
      }

      
      // Primero validar el token
      const { data: resetData, error: validateError } = await this.validateResetToken(token);
      if (validateError) throw validateError;
      
      // Hash de la nueva contraseña
      const encoder = new TextEncoder();
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(newPassword));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      // Actualizar contraseña del juez
      const { error: updateError } = await supabase
        .from('judges')
        .update({ password_hash: passwordHash })
        .eq('id', resetData.judge_id);
      
      if (updateError) throw updateError;
      
      // Marcar token como usado
      const { error: markError } = await supabase
        .from('password_resets')
        .update({
          used: true,
          used_at: new Date().toISOString()
        })
        .eq('id', resetData.id);
      
      if (markError) throw markError;
      
      return { data: { success: true }, error: null };
    } catch (error) {
      logger.error('❌ Error reseteando contraseña:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ==================== CLUB INVITATIONS ====================
export const clubInvitationService = {
  async generateInvitation(adminId, eventId, clubId, clubEmail, clubName) {
    try {

      // Generar código de invitación de 10 caracteres
      const code = 'CL' + Math.random().toString(36).substring(2, 10).toUpperCase();

      // Expiración en 30 días
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { data, error } = await supabase
        .from('club_invitations')
        .insert({
          code,
          admin_id: adminId,
          event_id: eventId,
          club_id: clubId || null,
          club_email: clubEmail || null,
          club_name: clubName || null,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error generando invitación de club:', error?.message);
      return { data: null, error };
    }
  },

  async validateInvitation(code) {
    try {
      if (!code || code.length < 5) {
        throw new Error('Código de invitación inválido');
      }


      const { data, error } = await supabase
        .from('club_invitations')
        .select('*, events(id, name, date, location)')
        .eq('code', code.toUpperCase())
        .single();

      if (error) throw new Error('Código de invitación no encontrado');

      if (data.used) {
        throw new Error('Este código de invitación ya ha sido utilizado');
      }

      if (new Date(data.expires_at) < new Date()) {
        throw new Error('Este código de invitación ha expirado');
      }

      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error validando invitación de club:', error?.message);
      return { data: null, error };
    }
  },

  async markAsUsed(invitationId, clubId) {
    try {

      const { data, error } = await supabase
        .from('club_invitations')
        .update({
          used: true,
          club_id: clubId,
          used_at: new Date().toISOString()
        })
        .eq('id', invitationId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error marcando invitación de club:', error?.message);
      return { data: null, error };
    }
  },

  async redeemInvitation(code, clubId) {
    try {

      // 1. Validar código
      const { data: invitation, error: valError } = await clubInvitationService.validateInvitation(code);
      if (valError) throw valError;

      // 2. Registrar club en event_clubs con status approved
      const { data: existing } = await supabase
        .from('event_clubs')
        .select('id')
        .eq('event_id', invitation.event_id)
        .eq('club_id', clubId)
        .single();

      if (!existing) {
        const { error: insertError } = await supabase
          .from('event_clubs')
          .insert({
            event_id: invitation.event_id,
            club_id: clubId,
            status: 'approved'
          });
        if (insertError) throw insertError;
      } else {
        // Si ya existe, actualizar a approved
        await supabase
          .from('event_clubs')
          .update({ status: 'approved' })
          .eq('event_id', invitation.event_id)
          .eq('club_id', clubId);
      }

      // 3. Marcar invitación como usada
      await clubInvitationService.markAsUsed(invitation.id, clubId);

      return { data: invitation, error: null };
    } catch (error) {
      logger.error('❌ Error canjeando invitación:', error?.message);
      return { data: null, error: error.message || error };
    }
  },

  async getByEvent(eventId) {
    try {
      const { data, error } = await supabase
        .from('club_invitations')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo invitaciones de club:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async getByAdmin(adminId) {
    try {
      const { data, error } = await supabase
        .from('club_invitations')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo invitaciones:', error?.message);
      return { data: [], error: error.message };
    }
  }
};

export const emailService = {
  // Template HTML del email de invitación
  _getInvitationHtml(judgeName, invitationCode, eventName) {
    const displayName = judgeName || 'Juez';
    return `
      <div style="font-family: 'Inter', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
        <div style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); padding: 32px 24px; border-radius: 12px 12px 0 0; text-align: center;">
          <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 700;">🏅 CopaGimnasta</h1>
          <p style="color: rgba(255,255,255,0.85); margin: 8px 0 0 0; font-size: 14px;">Plataforma de Gestión de Competencias de Gimnasia</p>
        </div>
        <div style="padding: 32px 24px; border: 1px solid #e5e7eb; border-top: none;">
          <h2 style="color: #111827; font-size: 20px; margin: 0 0 16px 0;">¡Hola ${displayName}!</h2>
          <p style="color: #4b5563; font-size: 15px; line-height: 1.6; margin: 0 0 16px 0;">Has sido invitado/a como <strong>Juez</strong> a la siguiente competencia:</p>
          <div style="background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%); padding: 16px 20px; border-radius: 10px; margin: 0 0 24px 0; border-left: 4px solid #8b5cf6;">
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #4338ca;">📅 ${eventName}</p>
          </div>
          <p style="color: #4b5563; font-size: 15px; line-height: 1.6; margin: 0 0 12px 0;">Utiliza el siguiente código para registrarte o acceder a la competencia:</p>
          <div style="background: #f9fafb; border: 2px dashed #8b5cf6; padding: 24px; border-radius: 12px; margin: 0 0 24px 0; text-align: center;">
            <p style="margin: 0 0 8px 0; font-size: 13px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px;">Tu código de invitación</p>
            <p style="font-size: 32px; font-weight: 800; color: #8b5cf6; margin: 0; letter-spacing: 4px; font-family: 'Courier New', monospace;">${invitationCode}</p>
          </div>
          <div style="background: #ecfdf5; padding: 12px 16px; border-radius: 8px; margin: 0 0 24px 0;">
            <p style="margin: 0; font-size: 13px; color: #065f46;">⏱ Este código es válido por <strong>30 días</strong>.</p>
          </div>
        </div>
        <div style="background: #f9fafb; padding: 20px 24px; border-radius: 0 0 12px 12px; border: 1px solid #e5e7eb; border-top: none; text-align: center;">
          <p style="margin: 0; font-size: 12px; color: #9ca3af;">© 2026 CopaGimnasta. Todos los derechos reservados.</p>
        </div>
      </div>
    `;
  },

  // Enviar email usando Resend API directamente
  async _sendViaResend() {
    throw new Error('El envío directo desde el navegador está deshabilitado por seguridad; utiliza el endpoint /api/* del servidor local.');
  },

  // Enviar email usando el endpoint serverless de Vercel (producción)
  async _sendViaServerless(recipientEmail, judgeName, invitationCode, eventName) {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'invitation',
        recipientEmail,
        judgeName: judgeName || 'Juez',
        invitationCode,
        eventName
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    return await response.json();
  },

  async sendInvitationEmail(judgeEmail, invitationCode, eventName) {
    return this.sendInvitationCode(judgeEmail, '', invitationCode, eventName);
  },

  async sendInvitationCode(judgeEmail, judgeName, invitationCode, eventName) {
    try {
      if (!judgeEmail || !invitationCode) {
        throw new Error('Email e invitación requeridos');
      }


      try {
        const result = await this._sendViaServerless(judgeEmail, judgeName, invitationCode, eventName);
        return { data: result, error: null };
      } catch (serverlessErr) {
        logger.error('❌ No se pudo enviar la invitación por el endpoint /api/send-invitation-email:', serverlessErr.message);
        return { data: null, error: 'No se pudo enviar la invitación. Asegura que el servidor local de /api esté activo.' };
      }
    } catch (error) {
      logger.error('❌ Error enviando email:', error?.message);
      return { data: null, error: error.message || String(error) };
    }
  },

  async sendPasswordResetEmail(judgeEmail, resetToken) {
    try {
      if (!judgeEmail || !resetToken) {
        throw new Error('Email y token requeridos');
      }

      
      const resetUrl = `${window.location.origin}/jueces/password-reset?token=${resetToken}`;
      
      const html = `
        <div style="font-family: 'Inter', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); padding: 32px 24px; border-radius: 12px 12px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 700;">🏅 CopaGimnasta</h1>
          </div>
          <div style="padding: 32px 24px; border: 1px solid #e5e7eb; border-top: none;">
            <h2 style="color: #111827; font-size: 20px; margin: 0 0 16px 0;">Restablecer Contraseña</h2>
            <p style="color: #4b5563; font-size: 15px; line-height: 1.6;">Has solicitado restablecer tu contraseña. Haz clic en el siguiente enlace:</p>
            <div style="text-align: center; margin: 24px 0;">
              <a href="${resetUrl}" style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; padding: 14px 32px; border-radius: 10px; text-decoration: none; font-weight: 600; font-size: 16px; display: inline-block;">
                Restablecer Contraseña
              </a>
            </div>
            <p style="color: #9ca3af; font-size: 13px;">Este enlace es válido por 24 horas. Si no solicitaste este cambio, ignora este correo.</p>
          </div>
          <div style="background: #f9fafb; padding: 20px 24px; border-radius: 0 0 12px 12px; border: 1px solid #e5e7eb; border-top: none; text-align: center;">
            <p style="margin: 0; font-size: 12px; color: #9ca3af;">© 2026 CopaGimnasta. Todos los derechos reservados.</p>
          </div>
        </div>
      `;

      try {
        const response = await fetch('/api/send-password-reset-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: judgeEmail, resetUrl })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Error enviando email de restablecimiento');
        }
        const result = await response.json();
        return { data: result, error: null };
      } catch (serverlessErr) {
        logger.error('❌ No se pudo enviar el email de restablecimiento por /api/send-password-reset-email:', serverlessErr.message);
        return { data: null, error: 'No se pudo enviar el email de restablecimiento. Asegura que el servidor local de /api esté activo.' };
      }
    } catch (error) {
      logger.error('❌ Error enviando email de reset:', error?.message);
      return { data: null, error: error.message || error };
    }
  },

  // Template HTML para invitación de club
  _getClubInvitationHtml(clubName, invitationCode, eventName) {
    const displayName = clubName || 'Club';
    return `
      <div style="font-family: 'Inter', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
        <div style="background: #3f3f46; padding: 32px 24px; border-radius: 12px 12px 0 0; text-align: center;">
          <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 700;">🏅 CopaGimnasta</h1>
          <p style="color: rgba(255,255,255,0.75); margin: 8px 0 0 0; font-size: 14px;">Plataforma de Gestión de Competencias de Gimnasia</p>
        </div>
        <div style="padding: 32px 24px; border: 1px solid #e4e4e7; border-top: none;">
          <h2 style="color: #18181b; font-size: 20px; margin: 0 0 16px 0;">¡Hola ${displayName}!</h2>
          <p style="color: #52525b; font-size: 15px; line-height: 1.6; margin: 0 0 16px 0;">Tu club ha sido invitado a participar en la siguiente competencia:</p>
          <div style="background: #f5f3ff; padding: 16px 20px; border-radius: 10px; margin: 0 0 24px 0; border-left: 4px solid #8b5cf6;">
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #3f3f46;">📅 ${eventName}</p>
          </div>
          <p style="color: #52525b; font-size: 15px; line-height: 1.6; margin: 0 0 12px 0;">Utiliza el siguiente código en tu <strong>Portal de Club</strong> para registrarte a la competencia:</p>
          <div style="background: #fafafa; border: 2px dashed #8b5cf6; padding: 24px; border-radius: 12px; margin: 0 0 24px 0; text-align: center;">
            <p style="margin: 0 0 8px 0; font-size: 13px; color: #71717a; text-transform: uppercase; letter-spacing: 1px;">Tu código de invitación</p>
            <p style="font-size: 32px; font-weight: 800; color: #8b5cf6; margin: 0; letter-spacing: 4px; font-family: 'Courier New', monospace;">${invitationCode}</p>
          </div>
          <div style="background: #ecfdf5; padding: 12px 16px; border-radius: 8px; margin: 0 0 16px 0;">
            <p style="margin: 0; font-size: 13px; color: #065f46;">⏱ Este código es válido por <strong>30 días</strong>.</p>
          </div>
          <div style="background: #f4f4f5; padding: 12px 16px; border-radius: 8px;">
            <p style="margin: 0; font-size: 13px; color: #52525b;">📋 <strong>Pasos:</strong> Inicia sesión en tu Portal de Club → Mis Competencias → Ingresar Código de Invitación → Pega el código → ¡Listo!</p>
          </div>
        </div>
        <div style="background: #fafafa; padding: 20px 24px; border-radius: 0 0 12px 12px; border: 1px solid #e4e4e7; border-top: none; text-align: center;">
          <p style="margin: 0; font-size: 12px; color: #a1a1aa;">© ${new Date().getFullYear()} CopaGimnasta. Todos los derechos reservados.</p>
        </div>
      </div>
    `;
  },

  async sendClubInvitationCode(clubEmail, clubName, invitationCode, eventName) {
    try {
      if (!clubEmail || !invitationCode) {
        throw new Error('Email del club y código requeridos');
      }


      const subject = `🏅 Invitación a Competencia — ${eventName} | CopaGimnasta`;
      const html = this._getClubInvitationHtml(clubName, invitationCode, eventName);

      try {
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'invitation',
            recipientEmail: clubEmail,
            judgeName: clubName || 'Club',
            invitationCode,
            eventName
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Error enviando invitación de club');
        }

        const result = await response.json();
        return { data: result, error: null };
      } catch (error) {
        logger.error('❌ Error enviando email de invitación de club:', error?.message);
        return { data: null, error: 'No se pudo enviar la invitación de club. Asegura que el servidor local de /api esté activo.' };
      }
    } catch (error) {
      logger.error('❌ Error enviando email de invitación de club:', error?.message);
      return { data: null, error: error.message || String(error) };
    }
  }
};

// ================================================================
// SERVICIO PARA CONFIGURACIÓN DE JUECES POR EVENTO/APARATO
// ================================================================

export const judgeConfigService = {
  // Obtener configuración de un juez para un evento y aparato
  async getConfig(judgeId, eventId, apparatus) {
    try {
      const { data, error } = await supabase
        .from('judge_event_configs')
        .select('*')
        .eq('judge_id', judgeId)
        .eq('event_id', eventId)
        .eq('apparatus', apparatus)
        .limit(1);

      if (error) throw error;
      return { data: data && data.length > 0 ? data[0] : null, error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo config de juez:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Obtener todas las configuraciones de un juez para un evento
  async getConfigsByEvent(judgeId, eventId) {
    try {
      const { data, error } = await supabase
        .from('judge_event_configs')
        .select('*')
        .eq('judge_id', judgeId)
        .eq('event_id', eventId);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo configs:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Guardar o actualizar configuración
  async saveConfig(judgeId, eventId, apparatus, templateType, columns, judgeRole = 'standard') {
    try {
      // Primero, eliminar cualquier configuración anterior de este juez para este evento
      const { error: deleteError } = await supabase
        .from('judge_event_configs')
        .delete()
        .eq('judge_id', judgeId)
        .eq('event_id', eventId);

      if (deleteError) {
        logger.error('Error eliminando configs anteriores:', deleteError?.message);
      }

      // Luego, insertar la nueva configuración
      const insertData = {
        judge_id: judgeId,
        event_id: eventId,
        apparatus: apparatus,
        template_type: templateType,
        columns: columns,
        judge_role: judgeRole,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('judge_event_configs')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error guardando config:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Eliminar configuración
  async deleteConfig(judgeId, eventId, apparatus) {
    try {
      const { error } = await supabase
        .from('judge_event_configs')
        .delete()
        .eq('judge_id', judgeId)
        .eq('event_id', eventId)
        .eq('apparatus', apparatus);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('❌ Error eliminando config:', error?.message);
      return { error: error.message };
    }
  }
};

// ================================================================
// SERVICIO PARA GESTIÓN DE ATLETAS POR EVENTO (event_gymnasts)
// ================================================================

export const eventGymnastService = {
  // Obtener atletas inscritas en un evento por un club
  async getByEventAndClub(eventId, clubId) {
    try {
      
      const { data, error } = await supabase
        .from('event_gymnasts')
        .select('*, gimnasts:gymnast_id(*)')
        .eq('event_id', eventId)
        .eq('club_id', clubId);
      
      if (error) throw error;
      
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo atletas del evento:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Obtener todas las atletas inscritas en un evento (para el organizador)
  async getByEvent(eventId) {
    try {
      
      const { data, error } = await supabase
        .from('event_gymnasts')
        .select('*, gimnasts:gymnast_id(*), clubs:club_id(id, name, email, location)')
        .eq('event_id', eventId);
      
      if (error) throw error;
      
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo atletas del evento:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Agregar una atleta a un evento
  async addGymnastToEvent(eventId, gymnastId, clubId) {
    try {
      
      const { data, error } = await supabase
        .from('event_gymnasts')
        .insert([{ event_id: eventId, gymnast_id: gymnastId, club_id: clubId }])
        .select()
        .single();
      
      if (error) {
        // Si es duplicado, no es error real
        if (error.code === '23505') {
          return { data: null, error: 'La atleta ya está inscrita en esta competencia' };
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error agregando atleta al evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Remover una atleta de un evento
  async removeGymnastFromEvent(eventId, gymnastId) {
    try {
      
      const { error } = await supabase
        .from('event_gymnasts')
        .delete()
        .eq('event_id', eventId)
        .eq('gymnast_id', gymnastId);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('❌ Error removiendo atleta del evento:', error?.message);
      return { error: error.message };
    }
  },

  // Agregar múltiples atletas a un evento (batch)
  async addMultipleToEvent(eventId, gymnastIds, clubId) {
    try {
      
      const records = gymnastIds.map(gid => ({
        event_id: eventId,
        gymnast_id: gid,
        club_id: clubId
      }));
      
      const { data, error } = await supabase
        .from('event_gymnasts')
        .upsert(records, { onConflict: 'event_id,gymnast_id' })
        .select();
      
      if (error) throw error;
      
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('❌ Error agregando atletas al evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Remover múltiples atletas de un evento (batch)
  async removeMultipleFromEvent(eventId, gymnastIds) {
    try {
      
      const { error } = await supabase
        .from('event_gymnasts')
        .delete()
        .eq('event_id', eventId)
        .in('gymnast_id', gymnastIds);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      logger.error('❌ Error removiendo atletas del evento:', error?.message);
      return { error: error.message };
    }
  },

  // Sincronizar atletas de un evento (reemplaza la selección actual)
  async syncEventGymnasts(eventId, gymnastIds, clubId) {
    try {
      
      // Eliminar las actuales del club
      const { error: deleteError } = await supabase
        .from('event_gymnasts')
        .delete()
        .eq('event_id', eventId)
        .eq('club_id', clubId);
      
      if (deleteError) throw deleteError;
      
      // Si no hay atletas que agregar, solo limpiar
      if (gymnastIds.length === 0) {
        return { data: [], error: null };
      }
      
      // Insertar las nuevas
      const records = gymnastIds.map(gid => ({
        event_id: eventId,
        gymnast_id: gid,
        club_id: clubId
      }));
      
      const { data, error } = await supabase
        .from('event_gymnasts')
        .insert(records)
        .select();
      
      if (error) throw error;
      
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('❌ Error sincronizando atletas del evento:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Obtener resumen de atletas por club para un evento (para el organizador)
  async getEventSummary(eventId) {
    try {
      
      const { data, error } = await supabase
        .from('event_gymnasts')
        .select('*, gimnasts:gymnast_id(id, name, level, franja, birth_date, category), clubs:club_id(id, name, email, location)')
        .eq('event_id', eventId);
      
      if (error) throw error;
      
      // Agrupar por club
      const clubMap = {};
      (data || []).forEach(record => {
        const clubId = record.club_id;
        if (!clubMap[clubId]) {
          clubMap[clubId] = {
            club: record.clubs,
            gymnasts: [],
            count: 0
          };
        }
        if (record.gimnasts) {
          clubMap[clubId].gymnasts.push(record.gimnasts);
          clubMap[clubId].count++;
        }
      });
      
      const summary = Object.values(clubMap).sort((a, b) => (a.club?.name || '').localeCompare(b.club?.name || ''));
      
      return { data: summary, error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo resumen del evento:', error?.message);
      return { data: [], error: error.message };
    }
  }
};

// ================================================================
// SERVICIO PARA GESTIÓN DE NIVELES
// ================================================================

export const levelService = {
  async getAllLevels() {
    try {
      
      const { data, error } = await supabase
        .from('levels')
        .select('*')
        .order('order_number', { ascending: true });
      
      if (error) throw error;
      
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo niveles:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async getEventLevels(eventId) {
    try {
      if (!eventId) throw new Error('Event ID requerido');
      
      
      const { data, error } = await supabase
        .from('event_levels')
        .select(`
          id,
          level_id,
          order_number,
          levels(id, name, description)
        `)
        .eq('event_id', eventId)
        .order('order_number', { ascending: true });
      
      if (error) throw error;
      
      const formatted = (data || []).map(el => ({
        id: el.id,
        level_id: el.level_id,
        name: el.levels?.name,
        description: el.levels?.description,
        order_number: el.order_number
      }));
      
      return { data: formatted, error: null };
    } catch (error) {
      logger.error('❌ Error obteniendo niveles del evento:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async setEventLevels(eventId, levelIds) {
    try {
      if (!eventId || !Array.isArray(levelIds) || levelIds.length === 0) {
        throw new Error('Event ID y array de level IDs requeridos');
      }

      
      // Primero verificar que los IDs existen en la tabla levels
      const { data: existingLevels, error: checkError } = await supabase
        .from('levels')
        .select('id')
        .in('id', levelIds);
      
      if (checkError) {
        throw new Error('Error verificando niveles: ' + checkError.message);
      }
      
      if (existingLevels?.length !== levelIds.length) {
        const existingIds = existingLevels?.map(l => l.id) || [];
        const missingIds = levelIds.filter(id => !existingIds.includes(id));
        throw new Error(`Solo ${existingLevels?.length} de ${levelIds.length} IDs existen. Falta: ${missingIds.join(', ')}`);
      }
      
      const { error: deleteError } = await supabase
        .from('event_levels')
        .delete()
        .eq('event_id', eventId);
      
      if (deleteError) throw deleteError;
      
      const levelRecords = levelIds.map((levelId, index) => ({
        event_id: eventId,
        level_id: levelId,
        order_number: index
      }));
      
      const { data, error } = await supabase
        .from('event_levels')
        .insert(levelRecords)
        .select();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      logger.error('❌ Error asignando niveles:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ============================================================
// Servicio de Autenticación de Clubes
// ============================================================
export const clubAuthService = {
  async getByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('clubs')
        .select('*')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo club por email:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async register(clubData) {
    try {

      const insertData = {
        name: clubData.name,
        location: clubData.location || '',
        email: clubData.email,
        password_hash: clubData.password_hash,
        phone: clubData.phone || '',
        is_active: false,
        approval_status: 'pending_approval'
      };

      // Agregar disciplinas si se proporcionan
      if (clubData.disciplines && clubData.disciplines.length > 0) {
        insertData.disciplines = clubData.disciplines;
      }

      // Agregar RRSS solo si se proporcionan
      if (clubData.facebook_url) insertData.facebook_url = clubData.facebook_url;
      if (clubData.instagram_url) insertData.instagram_url = clubData.instagram_url;
      if (clubData.twitter_url) insertData.twitter_url = clubData.twitter_url;

      let { data, error } = await supabase
        .from('clubs')
        .insert([insertData])
        .select()
        .single();
      
      // Si falla por columnas que no existen, reintentar sin ellas
      if (error && (error.code === 'PGRST204' || error.code === '42703')) {
        delete insertData.facebook_url;
        delete insertData.instagram_url;
        delete insertData.twitter_url;
        delete insertData.disciplines;
        const retry = await supabase
          .from('clubs')
          .insert([insertData])
          .select()
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      logger.error('Error registrando club:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async updatePassword(clubId, newPassword) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(newPassword);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const { error } = await supabase
        .from('clubs')
        .update({ password_hash: hashHex })
        .eq('id', clubId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error actualizando contraseña del club:', error?.message);
      return { error: error.message };
    }
  },

  async getEventsByClub(clubId) {
    try {

      const { data: eventClubs, error: ecError } = await supabase
        .from('event_clubs')
        .select('event_id, status')
        .eq('club_id', clubId);

      if (ecError) throw ecError;

      if (!eventClubs || eventClubs.length === 0) {
        return { data: [], error: null };
      }

      // Solo obtener los aprobados
      const approvedEventIds = eventClubs
        .filter(ec => ec.status === 'approved')
        .map(ec => ec.event_id);

      if (approvedEventIds.length === 0) {
        return { data: [], error: null };
      }

      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('id, name, date, location, discipline, category')
        .in('id', approvedEventIds)
        .order('date', { ascending: false });

      if (eventsError) throw eventsError;

      return { data: events || [], error: null };
    } catch (error) {
      logger.error('❌ Error en getEventsByClub:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getAllEventRequests(clubId) {
    try {
      const { data, error } = await supabase
        .from('event_clubs')
        .select('event_id, status, created_at')
        .eq('club_id', clubId);

      if (error) throw error;

      if (!data || data.length === 0) {
        return { data: [], error: null };
      }

      const eventIds = data.map(ec => ec.event_id);

      const { data: events, error: evError } = await supabase
        .from('events')
        .select('id, name, date, location, discipline')
        .in('id', eventIds);

      if (evError) throw evError;

      // Combinar datos
      const result = data.map(ec => {
        const event = events.find(e => e.id === ec.event_id);
        return {
          ...ec,
          event
        };
      });

      return { data: result, error: null };
    } catch (error) {
      logger.error('Error obteniendo solicitudes:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ============================================================
// Servicio de Organizadores
// ============================================================
export const organizerService = {
  async getByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('organizers')
        .select('*')
        .eq('email', email)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo organizador por email:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async register(orgData) {
    try {
      const insertData = {
        name: orgData.name,
        email: orgData.email,
        password_hash: orgData.passwordHash,
        phone: orgData.phone || '',
        organization: orgData.organization || '',
        is_active: true
      };

      const { data, error } = await supabase
        .from('organizers')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error registrando organizador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async updatePassword(organizerId, passwordHash) {
    try {
      const { error } = await supabase
        .from('organizers')
        .update({ password_hash: passwordHash })
        .eq('id', organizerId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error actualizando contraseña del organizador:', error?.message);
      return { error: error.message };
    }
  },

  async getById(organizerId) {
    try {
      const { data, error } = await supabase
        .from('organizers')
        .select('*')
        .eq('id', organizerId)
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo organizador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getPendingRegistrations() {
    try {
      const { data, error } = await supabase
        .from('organizers')
        .select('*')
        .eq('approval_status', 'pending_approval')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo organizadores pendientes:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async approveRegistration(organizerId, adminNotes = '') {
    try {
      const { data, error } = await supabase
        .from('organizers')
        .update({
          approval_status: 'approved',
          is_active: true,
          admin_notes: adminNotes,
          approved_at: new Date().toISOString()
        })
        .eq('id', organizerId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error aprobando organizador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async rejectRegistration(organizerId, adminNotes = '') {
    try {
      const { data, error } = await supabase
        .from('organizers')
        .update({
          approval_status: 'rejected',
          is_active: false,
          admin_notes: adminNotes,
          approved_at: new Date().toISOString()
        })
        .eq('id', organizerId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error rechazando organizador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(organizerId, updateData) {
    try {
      const { data, error } = await supabase
        .from('organizers')
        .update(updateData)
        .eq('id', organizerId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando organizador:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ============================================================
// Servicio de Acceso a Eventos (Códigos e Invitaciones)
// ============================================================
export const eventAccessService = {
  async validateCode(code) {
    try {
      const { data, error } = await supabase
        .from('access_codes')
        .select('*, events(id, name)')
        .eq('code', code.toUpperCase().trim())
        .eq('is_active', true)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error validando código:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async requestEventAccess(clubId, eventId, code) {
    try {
      // Check if already requested
      const { data: existing } = await supabase
        .from('event_clubs')
        .select('id, status')
        .eq('club_id', clubId)
        .eq('event_id', eventId)
        .single();

      if (existing) {
        if (existing.status === 'approved') return { error: 'Ya tienes acceso a esta competencia' };
        if (existing.status === 'pending') return { error: 'Ya tienes una solicitud pendiente para esta competencia' };
        // If rejected, allow re-request by updating
        const { error: updateError } = await supabase
          .from('event_clubs')
          .update({ status: 'pending', code_used: code })
          .eq('id', existing.id);
        if (updateError) throw updateError;
        return { error: null };
      }

      const { error } = await supabase
        .from('event_clubs')
        .insert([{ club_id: clubId, event_id: eventId, status: 'pending', code_used: code }]);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error solicitando acceso:', error?.message);
      return { error: error.message };
    }
  },

  async getEventCode(eventId) {
    try {
      const { data, error } = await supabase
        .from('access_codes')
        .select('*')
        .eq('event_id', eventId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo código de competencia:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async generateEventCode(eventId) {
    try {
      // Generate a random 8-char code
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();

      // Deactivate any existing codes for this event
      await supabase
        .from('access_codes')
        .update({ is_active: false })
        .eq('event_id', eventId);

      const { data, error } = await supabase
        .from('access_codes')
        .insert([{ event_id: eventId, code, is_active: true }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error generando código:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getPendingRequests() {
    try {
      const { data, error } = await supabase
        .from('event_clubs')
        .select('*, clubs(id, name, email, location), events(id, name, date, discipline)')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo solicitudes pendientes:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async getRequestsByEvents(eventIds) {
    try {
      if (!eventIds || eventIds.length === 0) return { data: [], error: null };
      const { data, error } = await supabase
        .from('event_clubs')
        .select('*, clubs(id, name, email, location, phone, facebook_url, instagram_url, twitter_url, is_federated), events(id, name, date, federated_only)')
        .in('event_id', eventIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo solicitudes por competencias:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async approveRequest(eventClubId) {
    try {
      const { data, error } = await supabase
        .from('event_clubs')
        .update({ status: 'approved' })
        .eq('id', eventClubId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error aprobando solicitud:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async rejectRequest(eventClubId) {
    try {
      const { data, error } = await supabase
        .from('event_clubs')
        .update({ status: 'rejected' })
        .eq('id', eventClubId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error rechazando solicitud:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ============================================================
// Servicio de Autenticación de Entrenadores
// ============================================================
export const coachAuthService = {
  async getByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .select('*')
        .eq('email', email)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo entrenador por email:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async register(coachData) {
    try {
      const insertData = {
        name: coachData.name,
        email: coachData.email,
        password_hash: coachData.password_hash,
        phone: coachData.phone || '',
        location: coachData.location || '',
        bio: coachData.bio || '',
        specialties: coachData.specialties || [],
        experience_years: coachData.experience_years || 0,
        looking_for_club: false,
        is_active: true
      };

      const { data, error } = await supabase
        .from('coaches')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error registrando entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async updatePassword(coachId, newPassword) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(newPassword);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const { error } = await supabase
        .from('coaches')
        .update({ password_hash: hashHex })
        .eq('id', coachId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error actualizando contraseña del entrenador:', error?.message);
      return { error: error.message };
    }
  }
};

// ============================================================
// Servicio de Entrenadores (perfil, clubes, etc.)
// ============================================================
export const coachService = {
  async getById(coachId) {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .select('*')
        .eq('id', coachId)
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(coachId, updateData) {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .update({ ...updateData, updated_at: new Date().toISOString() })
        .eq('id', coachId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async create(coachData) {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .insert(coachData)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getByAuthUserId(authUserId) {
    try {
      const { data, error } = await db('coaches')
        .select('*')
        .eq('auth_user_id', authUserId)
        .limit(1)
        .execute();
      if (error) throw error;
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      logger.error('Error obteniendo trainer por auth_user_id:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getByEmail(email) {
    try {
      // Usar db() que tiene todas las operaciones
      const { data, error } = await db('coaches')
        .select('*')
        .eq('email', (email || '').toLowerCase())
        .limit(1)
        .execute();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error obteniendo trainer por email:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async toggleLookingForClub(coachId, value) {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .update({ looking_for_club: value, updated_at: new Date().toISOString() })
        .eq('id', coachId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error toggle busco club:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Solicitar acceso a un club
  async requestClubAccess(coachId, clubId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .insert([{ coach_id: coachId, club_id: clubId, status: 'pending' }])
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (error.message?.includes('duplicate') || error.code === '23505') {
        return { data: null, error: 'Ya tienes una solicitud para este club' };
      }
      logger.error('Error solicitando acceso a club:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Obtener clubes del entrenador (aprobados)
  async getMyClubs(coachId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .select('*, clubs(*)')
        .eq('coach_id', coachId)
        .eq('status', 'approved');
      if (error) throw error;
      // Mantenemos el flag de IA a nivel acceso para evaluar hasPoseAccess
      // junto con clubs.pose_analysis_enabled (AND de ambos).
      return {
        data: (data || []).map(d => ({
          ...d.clubs,
          access_id: d.id,
          role: d.role,
          access_pose_analysis_enabled: !!d.pose_analysis_enabled,
        })),
        error: null,
      };
    } catch (error) {
      logger.error('Error obteniendo clubes del entrenador:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Obtener historial de clubes (todos los estados)
  async getClubHistory(coachId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .select('*, clubs(id, name, location, email)')
        .eq('coach_id', coachId)
        .order('requested_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo historial de clubes:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Obtener todas las solicitudes pendientes del entrenador
  async getMyRequests(coachId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .select('*, clubs(id, name, location)')
        .eq('coach_id', coachId)
        .order('requested_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo solicitudes:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Buscar clubes disponibles para solicitar acceso
  async searchClubs(query) {
    try {
      let q = supabase
        .from('clubs')
        .select('id, name, location, email, disciplines, facebook_url, instagram_url, twitter_url')
        .eq('is_active', true)
        .eq('approval_status', 'approved');

      if (query && query.trim()) {
        q = q.or(`name.ilike.%${query.trim()}%,location.ilike.%${query.trim()}%`);
      }

      const { data, error } = await q.order('name').limit(50);
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error buscando clubes:', error?.message);
      return { data: [], error: error.message };
    }
  }
};

// ============================================================
// Servicio de Bloques de Horario
// ============================================================
export const scheduleBlockService = {
  async getByClub(clubId) {
    try {
      if (!clubId || clubId === 'undefined' || clubId === 'null') return { data: [], error: null };
      const { data, error } = await supabase
        .from('schedule_blocks')
        .select('*')
        .eq('club_id', clubId)
        .eq('is_active', true)
        .order('day_of_week')
        .order('start_time');
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo bloques de horario:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async getBlockAthletes(blockId) {
    try {
      const { data, error } = await supabase
        .from('athlete_schedule_assignments')
        .select('*, gimnasts(id, name, level, franja, category, birth_date)')
        .eq('schedule_block_id', blockId);
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo atletas del bloque:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async addAthleteToBlock(gymnastId, blockId, clubId) {
    try {
      const { data, error } = await supabase
        .from('athlete_schedule_assignments')
        .upsert([{ gymnast_id: gymnastId, schedule_block_id: blockId, club_id: clubId }], { onConflict: 'gymnast_id,schedule_block_id' })
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error asignando atleta al bloque:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async removeAthleteFromBlock(gymnastId, blockId) {
    try {
      const { error } = await supabase
        .from('athlete_schedule_assignments')
        .delete()
        .eq('gymnast_id', gymnastId)
        .eq('schedule_block_id', blockId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error removiendo atleta del bloque:', error?.message);
      return { error: error.message };
    }
  },

  async create(blockData) {
    try {
      const { data, error } = await supabase
        .from('schedule_blocks')
        .insert([blockData])
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando bloque de horario:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async update(blockId, updateData) {
    try {
      const { data, error } = await supabase
        .from('schedule_blocks')
        .update(updateData)
        .eq('id', blockId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando bloque:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async delete(blockId) {
    try {
      const { error } = await supabase
        .from('schedule_blocks')
        .update({ is_active: false })
        .eq('id', blockId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error eliminando bloque:', error?.message);
      return { error: error.message };
    }
  },

  // --- Coach-Block assignments ---
  async getBlockCoaches(blockId) {
    try {
      const { data, error } = await supabase
        .from('coach_schedule_assignments')
        .select('*, coaches(id, name, email, phone, specialties)')
        .eq('schedule_block_id', blockId);
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo entrenadores del bloque:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async addCoachToBlock(coachId, blockId, clubId) {
    try {
      const { data, error } = await supabase
        .from('coach_schedule_assignments')
        .upsert([{ coach_id: coachId, schedule_block_id: blockId, club_id: clubId }], { onConflict: 'coach_id,schedule_block_id' })
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error asignando entrenador al bloque:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async removeCoachFromBlock(coachId, blockId) {
    try {
      const { error } = await supabase
        .from('coach_schedule_assignments')
        .delete()
        .eq('coach_id', coachId)
        .eq('schedule_block_id', blockId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error removiendo entrenador del bloque:', error?.message);
      return { error: error.message };
    }
  }
};

// ============================================================
// Servicio de Asistencia
// ============================================================
export const attendanceService = {
  // --- Atletas ---
  async markAthleteAttendance(records) {
    try {
      const { data, error } = await supabase
        .from('athlete_attendance')
        .upsert(records, { onConflict: 'gymnast_id,schedule_block_id,date' })
        .select();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error marcando asistencia de atleta:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getAthleteAttendance(clubId, dateFrom, dateTo) {
    try {
      let q = supabase
        .from('athlete_attendance')
        .select('*, gimnasts(id, name, category, level)')
        .eq('club_id', clubId);

      if (dateFrom) q = q.gte('date', dateFrom);
      if (dateTo) q = q.lte('date', dateTo);

      const { data, error } = await q.order('date', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo asistencia de atletas:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async getAthleteAttendanceByMonth(clubId, year, month) {
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const { data, error } = await supabase
        .from('athlete_attendance')
        .select('*, gimnasts(id, name, category, level)')
        .eq('club_id', clubId)
        .gte('date', dateFrom)
        .lte('date', dateTo)
        .order('date', { ascending: true });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo asistencia mensual:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async justifyAbsence(attendanceId, justification, rescheduledDate) {
    try {
      const updateData = { status: 'justified', justification };
      if (rescheduledDate) {
        updateData.status = 'rescheduled';
        updateData.rescheduled_date = rescheduledDate;
      }
      const { data, error } = await supabase
        .from('athlete_attendance')
        .update(updateData)
        .eq('id', attendanceId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error justificando ausencia:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // --- Entrenadores ---
  async markCoachAttendance(records) {
    try {
      logger.info('markCoachAttendance records:', JSON.stringify(records));
      const { data, error } = await supabase
        .from('coach_attendance')
        .upsert(records, { onConflict: 'coach_id,date' })
        .select();
      if (error) {
        logger.error('Upsert error details:', error.code, error.message, error.details, error.hint);
        throw error;
      }
      logger.info('markCoachAttendance success:', data?.length, 'records');
      return { data, error: null };
    } catch (error) {
      logger.error('Error marcando asistencia de entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async markCoachCheckOut(coachId, clubId, date) {
    try {
      logger.info('markCoachCheckOut:', { coachId, clubId, date });
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('coach_attendance')
        .update({ check_out_time: now })
        .eq('coach_id', coachId)
        .eq('club_id', clubId)
        .eq('date', date)
        .select()
        .single();
      if (error) {
        logger.error('CheckOut error details:', error.code, error.message, error.details);
        throw error;
      }
      logger.info('markCoachCheckOut success:', data?.id);
      return { data, error: null };
    } catch (error) {
      logger.error('Error marcando salida de entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getCoachAttendance(clubId, dateFrom, dateTo) {
    try {
      let q = supabase
        .from('coach_attendance')
        .select('*, coaches(id, name, email, phone, specialties)')
        .eq('club_id', clubId);

      if (dateFrom) q = q.gte('date', dateFrom);
      if (dateTo) q = q.lte('date', dateTo);

      const { data, error } = await q.order('date', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo asistencia de entrenadores:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async getCoachAttendanceByMonth(clubId, year, month) {
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const { data, error } = await supabase
        .from('coach_attendance')
        .select('*, coaches(id, name, email, phone, specialties)')
        .eq('club_id', clubId)
        .gte('date', dateFrom)
        .lte('date', dateTo)
        .order('date', { ascending: true });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo asistencia mensual de entrenadores:', error?.message);
      return { data: [], error: error.message };
    }
  }
};

// ============================================================
// Servicio de Ofertas de Empleo
// ============================================================
export const jobService = {
  // Para clubes: CRUD de ofertas
  async createPosting(postingData) {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .insert([postingData])
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error creando oferta:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getPostingsByClub(clubId) {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*, job_applications(id, coach_id, status, coaches(id, name, email))')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo ofertas del club:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async updatePosting(postingId, updateData) {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .update({ ...updateData, updated_at: new Date().toISOString() })
        .eq('id', postingId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error actualizando oferta:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async deletePosting(postingId) {
    try {
      const { error } = await supabase
        .from('job_postings')
        .delete()
        .eq('id', postingId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error eliminando oferta:', error?.message);
      return { error: error.message };
    }
  },

  // Para entrenadores: buscar y postularse
  async getActivePostings(query) {
    try {
      let q = supabase
        .from('job_postings')
        .select('*, clubs(id, name, location)')
        .eq('is_active', true);

      if (query && query.trim()) {
        q = q.or(`title.ilike.%${query.trim()}%,description.ilike.%${query.trim()}%`);
      }

      const { data, error } = await q.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error buscando ofertas:', error?.message);
      return { data: [], error: error.message };
    }
  },

  async applyToPosting(postingId, coachId, message) {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .insert([{ job_posting_id: postingId, coach_id: coachId, message: message || '' }])
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (error.message?.includes('duplicate') || error.code === '23505') {
        return { data: null, error: 'Ya te postulaste a esta oferta' };
      }
      logger.error('Error postulándose:', error?.message);
      return { data: null, error: error.message };
    }
  },

  async getMyApplications(coachId) {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select('*, job_postings(*, clubs(id, name, location))')
        .eq('coach_id', coachId)
        .order('applied_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo postulaciones:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Para clubes: gestionar postulaciones
  async resolveApplication(applicationId, status) {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .update({ status, resolved_at: new Date().toISOString() })
        .eq('id', applicationId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error resolviendo postulación:', error?.message);
      return { data: null, error: error.message };
    }
  }
};

// ============================================================
// Servicio de acceso Coach-Club (para el club)
// ============================================================
export const coachClubService = {
  // Obtener solicitudes de entrenadores para un club
  async getRequestsByClub(clubId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .select('*, coaches(id, name, email, phone, specialties, experience_years, photo_url, bio, cv_url, location)')
        .eq('club_id', clubId)
        .order('requested_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error obteniendo solicitudes de entrenadores:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Aprobar solicitud
  async approveRequest(accessId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .update({ status: 'approved', resolved_at: new Date().toISOString() })
        .eq('id', accessId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error aprobando solicitud de entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Rechazar solicitud
  async rejectRequest(accessId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .update({ status: 'rejected', resolved_at: new Date().toISOString() })
        .eq('id', accessId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error rechazando solicitud de entrenador:', error?.message);
      return { data: null, error: error.message };
    }
  },

  // Obtener entrenadores aprobados del club
  async getApprovedCoaches(clubId) {
    try {
      const { data, error } = await supabase
        .from('coach_club_access')
        .select('*, coaches(id, name, email, phone, specialties, experience_years, photo_url, bio, cv_url, location)')
        .eq('club_id', clubId)
        .eq('status', 'approved');
      if (error) throw error;
      return { data: (data || []).map(d => ({ ...d.coaches, access_id: d.id, role: d.role })), error: null };
    } catch (error) {
      logger.error('Error obteniendo entrenadores del club:', error?.message);
      return { data: [], error: error.message };
    }
  },

  // Remover entrenador del club
  async removeCoach(accessId) {
    try {
      const { error } = await supabase
        .from('coach_club_access')
        .delete()
        .eq('id', accessId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error removiendo entrenador:', error?.message);
      return { error: error.message };
    }
  }
};
